import { createFileRoute, redirect, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { usePublicArticles, useTopics } from '@/queries/articles';
import { ArticleList } from '@/components/ArticleList';
import { CircularLoader } from '@/components/CircularLoader';
import SearchIcon from '@/assets/common/search.svg?react';
import ArrowLeftIcon from '@/assets/common/arrow-left.svg?react';
import styles from './public.module.scss';

export const Route = createFileRoute('/articles/public')({
  component: PublicArticlesPage,
  beforeLoad: ({ context }) => {
    const { user, authLoading } = context as { user: any; authLoading: boolean };
    if (authLoading) return;
    if (!user) {
      throw redirect({ to: '/' });
    }
  },
});

function PublicArticlesPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTopic, setSelectedTopic] = useState<string | null>(null);

  const { data: topics } = useTopics();
  const { data: articles, isLoading, error } = usePublicArticles({
    search: searchTerm || undefined,
    topic: selectedTopic || undefined,
  });

  return (
    <div className={styles.publicArticles}>
      <div className={styles.publicArticles__header}>
        <Link to="/articles" className={styles.publicArticles__backButton}>
          <ArrowLeftIcon />
          Back to My Articles
        </Link>
        <h1 className={styles.publicArticles__title}>Browse Articles</h1>
        <p className={styles.publicArticles__subtitle}>
          Discover articles from the Knowblemind community
        </p>
      </div>

      <div className={styles.publicArticles__filters}>
        <div className={styles.publicArticles__search}>
          <SearchIcon className={styles.publicArticles__searchIcon} />
          <input
            type="text"
            placeholder="Search articles..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.publicArticles__searchInput}
          />
        </div>

        <div className={styles.publicArticles__topics}>
          <button
            className={`${styles.publicArticles__topicButton} ${
              !selectedTopic ? styles['publicArticles__topicButton--active'] : ''
            }`}
            onClick={() => setSelectedTopic(null)}
          >
            All
          </button>
          {topics?.map((topic) => (
            <button
              key={topic.id}
              className={`${styles.publicArticles__topicButton} ${
                selectedTopic === topic.name ? styles['publicArticles__topicButton--active'] : ''
              }`}
              onClick={() => setSelectedTopic(topic.name)}
            >
              {topic.name}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className={styles.publicArticles__loading}>
          <CircularLoader />
        </div>
      ) : error ? (
        <div className={styles.publicArticles__error}>
          Error loading articles: {error.message}
        </div>
      ) : (
        <ArticleList
          articles={articles || []}
          showAuthor
          emptyMessage="No public articles found. Try a different search or topic."
        />
      )}
    </div>
  );
}
